import { useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { Button } from '~/components/button';
import { COMMUNITY_SOLAR } from '~/components/dialogs';
import { type ImageType, ImageSelector, HALFMOON_SOLAR } from '~/components/image';
import { ImageUploader } from '~/components/image-uploader';
import { TwoColumnGrid } from '~/components/layout';
import { Separator } from '~/components/separator';
import { Title, Description, Ul } from '~/components/typography';
import { useCopy } from '~/hooks';
import { User, Utility } from '~/typings/context';
import { UTILITY_IDS } from '~/utils/constants';
import { UploadedImage } from '~/utils/files';
import { DualBillingModal } from '../modal/dual-billing.component';

export const BillUpload = ({
  context,
  goTo,
  next,
  pending,
  onDelete,
  onUploadFailure,
  onUploadSuccess,
  overrideCopy,
}: BillUploadProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const isDominionVA = context.utility.id === UTILITY_IDS['Dominion Energy VA'];
  const goToCredentials = goTo?.credentials;
  const enoughFiles = context.pagesRequired <= context.files.filter(f => f.uploadId).length;
  const [showExplainerModal, setShowExplainerModal] = useState<boolean>(false);

  const popUpOverrideCopy = () => ({
    next: 'Upload your bill',
    descriptionFirstLine: 'There was an issue connecting to your utility.',
    descriptionSecondLine:
      'To continue signing up with Arcadia, please upload page 1 and page 2 of your most recent Dominion bill.',
  });

  const defaultOverrideCopy = () => ({
    next: 'Upload your bill',
    descriptionFirstLine: 'There was an issue connecting to your utility.',
    descriptionSecondLine:
      'To continue signing up with Arcadia, please upload a photo of your most recent power bill.',
  });
  useEffect(() => {
    setShowExplainerModal(true);
  }, []);

  return (
    <TwoColumnGrid testId="screen-BillUpload">
      {showExplainerModal && (
        <DualBillingModal
          hide={() => {
            setShowExplainerModal(false);
          }}
          isBillUploadDialog
          overrideCopy={isDominionVA ? popUpOverrideCopy : defaultOverrideCopy}
          context={context}
        />
      )}
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description>{copy.description}</Description>
        <Description>{copy.descriptionTwo}</Description>
        <Ul mb={5}>
          {copy.bullets.map(text => (
            <li key={text}>{text}</li>
          ))}
        </Ul>
        <ImageUploader
          acceptFileTypes={{ 'image/*': ['.jpg', '.jpeg', '.png', '.heic'] }}
          copy={{ documentType: 'power bill' }}
          uploadedImages={context.files}
          maxImages={context.maxPages}
          pageType="numbered"
          onDelete={onDelete}
          onUploadFailure={onUploadFailure}
          onUploadSuccess={onUploadSuccess}
          pending={pending}
        />
        <Button
          disabled={!enoughFiles || pending}
          mt={7}
          mode="primary"
          onClick={() => next()}
          testId="bill-upload-next"
        >
          {copy.next}
        </Button>
        {goToCredentials && (
          <>
            <Separator innerText="or" />
            <Description mb={7}>{copy.utilitySyncDescription}</Description>
            <Button mode="default" onClick={() => goToCredentials()}>
              {copy.utilitySyncCTA}
            </Button>
          </>
        )}
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const defaultCopy = (context: BillUploadContext) => ({
  bullets: ['Utility account number', 'Address', 'Meter number', 'Monthly usage summary'],
  description:
    context.utility.id === UTILITY_IDS['Dominion Energy VA']
      ? `Please include the ${
          context.pagesRequired === 1 ? 'front page' : 'first two pages'
        } of your most recent Dominion bill.`
      : `Please include the ${
          context.pagesRequired === 1 ? 'first page' : 'first two pages'
        } of your most recent power bill.`,
  descriptionTwo: `The ${
    context.pagesRequired === 1 ? 'photo' : 'photos'
  } should show the following information:`,
  learnMoreModal: COMMUNITY_SOLAR,
  imageType: HALFMOON_SOLAR as ImageType,
  next: 'Continue',
  title: `Upload ${context.pagesRequired === 1 ? 'a photo' : 'photos'} of your power bill.`,
  utilitySyncCTA: 'Enter utility login credentials',
  utilitySyncDescription:
    'If you have an online utility account, you can enter your credentials to securely connect it with Arcadia to verify your eligibility and keep track of the monthly savings you earn with community solar.',
});

export type BillUploadCopy = ReturnType<typeof defaultCopy>;

export interface BillUploadProps {
  context: BillUploadContext;
  goTo?: {
    credentials?: () => void;
  };
  next: () => void;
  onDelete: (id: string) => void;
  onUploadFailure: (file: FileRejection) => void;
  onUploadSuccess: (file: File, base64Image: string) => void;
  overrideCopy?: (context: BillUploadContext) => Partial<BillUploadCopy>;
  pending: boolean;
}

export type PagesRequired = 1 | 2;
export interface BillUploadContext {
  files: UploadedImage[];
  pagesRequired: PagesRequired;
  maxPages: PagesRequired;
  utility: Utility;
  user: User;
  funnelId?: string;
  promoCode?: string;
}
